import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Popover, Segmented } from 'antd';
import { CloseOutlined, DownOutlined, SearchOutlined } from '@ant-design/icons';
import { formatEuro } from '../../utils/directOrderStatus';

const TOP_COUNT = 5;

const SORTS = {
  orders: (a, b) => b.count - a.count || a.name.localeCompare(b.name),
  value: (a, b) => b.total - a.total || a.name.localeCompare(b.name),
  name: (a, b) => a.name.localeCompare(b.name),
};

/** Festival name with the part matching the search highlighted. */
const Highlight = ({ text, term }) => {
  if (!term) return text;
  const i = text.toLowerCase().indexOf(term);
  if (i === -1) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark>{text.slice(i, i + term.length)}</mark>
      {text.slice(i + term.length)}
    </>
  );
};

/** The searchable list behind "All festivals". Arrow keys move, Enter picks, Escape closes. */
const FestivalList = ({ festivals, value, onPick, onClose }) => {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('orders');
  const [active, setActive] = useState(0);
  const listRef = useRef(null);

  const term = query.trim().toLowerCase();
  const items = useMemo(
    () => festivals.filter((f) => !term || f.name.toLowerCase().includes(term)).sort(SORTS[sort]),
    [festivals, term, sort]
  );

  useEffect(() => { setActive(0); }, [term, sort]);

  // keep the highlighted row in view while moving with the keyboard
  useEffect(() => {
    const row = listRef.current?.querySelector(`[data-index="${active}"]`);
    row?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && items[active]) {
      e.preventDefault();
      onPick(items[active].name);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="do-fest-pop">
      <Input
        autoFocus
        allowClear
        prefix={<SearchOutlined aria-hidden="true" />}
        placeholder={`Search ${festivals.length} festivals`}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        aria-label="Search festivals"
        aria-controls="do-fest-options"
        aria-activedescendant={items[active] ? `do-fest-opt-${active}` : undefined}
      />
      <div className="do-fest-pop__sort">
        <span>Sort by</span>
        <Segmented
          size="small"
          value={sort}
          onChange={setSort}
          options={[
            { value: 'orders', label: 'Orders' },
            { value: 'value', label: 'Value' },
            { value: 'name', label: 'A–Z' },
          ]}
          aria-label="Sort festivals"
        />
      </div>
      {items.length === 0 ? (
        <p className="do-fest-pop__none">No festival matches “{query.trim()}”.</p>
      ) : (
        <ul className="do-fest-pop__list" id="do-fest-options" role="listbox" aria-label="Festivals" ref={listRef}>
          {items.map((f, i) => (
            <li key={f.name} role="presentation">
              <button
                type="button"
                id={`do-fest-opt-${i}`}
                role="option"
                aria-selected={value === f.name}
                data-index={i}
                className={`do-fest-opt${i === active ? ' is-active' : ''}${value === f.name ? ' is-on' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => onPick(f.name)}
              >
                <span className="do-fest-opt__name"><Highlight text={f.name} term={term} /></span>
                <span className="do-fest-opt__count do-num">{f.count} {f.count === 1 ? 'order' : 'orders'}</span>
                <span className="do-fest-opt__total do-num">{formatEuro(f.total)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Festival filter for the direct orders list: All, the busiest festivals as chips, and an
 * "All festivals" button that opens a searchable list. A festival picked from the list that
 * isn't one of the busiest shows as its own chip with a clear button.
 *
 * festivals: [{ name, count, total }]; value: the selected festival name or 'all'.
 */
const FestivalFilter = ({ festivals, value, totalCount, onChange }) => {
  const [open, setOpen] = useState(false);

  const top = useMemo(() => [...festivals].sort(SORTS.orders).slice(0, TOP_COUNT), [festivals]);
  const pickedOutsideTop = value !== 'all' && !top.some((f) => f.name === value);

  const pick = (name) => {
    onChange(name);
    setOpen(false);
  };

  return (
    <div className="do-fest" role="group" aria-label="Filter by festival">
      {/* the busiest festivals scroll if the row is narrow; the picked chip and the list button stay put */}
      <div className="do-fest__scroll">
        <button
          type="button"
          className={`do-fest-chip${value === 'all' ? ' is-on' : ''}`}
          aria-pressed={value === 'all'}
          onClick={() => onChange('all')}
        >
          All <em className="do-num">{totalCount}</em>
        </button>
        {top.map((f) => (
          <button
            key={f.name}
            type="button"
            className={`do-fest-chip${value === f.name ? ' is-on' : ''}`}
            aria-pressed={value === f.name}
            onClick={() => onChange(f.name)}
          >
            {f.name} <em className="do-num">{f.count}</em>
          </button>
        ))}
      </div>
      {pickedOutsideTop && (
        <span className="do-fest-chip is-picked">
          {value}
          <button type="button" className="do-fest-chip__clear" aria-label={`Clear ${value}`} onClick={() => onChange('all')}>
            <CloseOutlined aria-hidden="true" />
          </button>
        </span>
      )}
      {festivals.length > TOP_COUNT && (
        <Popover
          open={open}
          onOpenChange={setOpen}
          trigger="click"
          placement="bottomRight"
          arrow={false}
          destroyOnHidden
          rootClassName="do-scope do-fest-overlay"
          content={<FestivalList festivals={festivals} value={value} onPick={pick} onClose={() => setOpen(false)} />}
        >
          <button type="button" className="do-fest-chip is-picker" aria-haspopup="listbox" aria-expanded={open}>
            All festivals <em className="do-num">{festivals.length}</em>
            <DownOutlined aria-hidden="true" className="do-fest-chip__caret" />
          </button>
        </Popover>
      )}
    </div>
  );
};

export default FestivalFilter;
